import { Badge } from "@/components/Badge";
import { Button } from "@/components/Button";
import Table from "@/components/Table/Table";
import { useGetPoliciesTable } from "@/requests/hooks/comply";
import { formatDate } from "@/utils/helpers";
import { faCaretRight } from "@fortawesome/pro-solid-svg-icons";

import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { tv } from "tailwind-variants";

interface IProps {
  activeLanguage?: string;
}

const styles = tv({
  slots: {
    base: "px-20 md:px-[unset]",
    emptyColumn: "pl-20 md:pl-40",
  },
});

const PoliciesTable = ({ activeLanguage }: IProps) => {
  const { t } = useTranslation();
  const { search } = useLocation();
  const navigate = useNavigate();
  const { base, emptyColumn } = styles();

  const navigateToPolicies = (path: string) => () => {
    navigate(path, { state: search });
  };

  const columnHelper = createColumnHelper<IPolicy>();

  const columns = [
    columnHelper.accessor("name", {
      header: t("comply.policies.table.columns.name"),
    }),
    columnHelper.accessor("createdAt", {
      header: t("comply.policies.table.columns.createdAt"),
      cell: info => {
        const date = info.getValue();
        return !!date ? formatDate(date) : renderEmptyColumn();
      },
    }),
    columnHelper.accessor("updatedAt", {
      header: t("comply.policies.table.columns.updatedAt"),
      cell: info => {
        const date = info.getValue();
        return !!date ? formatDate(date) : renderEmptyColumn();
      },
    }),
    columnHelper.accessor("updatedBy", {
      header: t("comply.policies.table.columns.updatedBy"),
      cell: info => {
        const owner = info.getValue();
        return owner || renderEmptyColumn();
      },
    }),
    columnHelper.accessor("active", {
      header: t("comply.policies.table.columns.status"),
      cell: info => (
        <Badge variant={!!info.getValue() ? "success" : "error"}>
          {!!info.getValue()
            ? t("comply.policies.table.rows.enabled")
            : t("comply.policies.table.rows.disabled")}
        </Badge>
      ),
    }),
    columnHelper.accessor("id", {
      header: t("comply.policies.table.columns.edit"),
      cell: info => {
        const textOrPDFPolicy = !!info.row.original.content ? "text" : "pdf";
        return (
          <Button
            as="button"
            variant="link"
            onClick={navigateToPolicies(
              `/comply/policies/edit/${textOrPDFPolicy}/${info.getValue()}`,
            )}
            icon={faCaretRight}
            iconPosition="end"
            iconClassName="text-secondary">
            {t("comply.policies.table.rows.edit")}
          </Button>
        );
      },
    }),
  ];

  const renderEmptyColumn = () => <div className={emptyColumn()}>-</div>;

  return (
    <div className={base()}>
      <Table
        queryHook={useGetPoliciesTable}
        queryKey={["comply", "list-policies"]}
        columns={columns}
        filters={{
          language: activeLanguage || "",
        }}
        isSearchEnabled={false}
        isPageInformationEnabled={false}
      />
    </div>
  );
};

export default PoliciesTable;
